import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import DebtPurchaseDialog from "./DebtPurchaseDialog";

const fmt = (v) => `£${Number(v||0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DebtPaymentHistoryDialog({ open, onClose, debt, api, onDataChanged }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState("");
  const [editing, setEditing] = useState(null);   // purchase row being edited
  const [confirmRow, setConfirmRow] = useState(null);  // row pending delete
  const [deleting, setDeleting] = useState(false);

  const reload = () => {
    if (!debt?.id) return;
    setLoading(true); setErr("");
    axios.get(`${api}/debts/${debt.id}/payments`)
      .then((res) => setData(res.data))
      .catch(() => setErr("Failed to load payment history"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !debt?.id) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debt?.id, api]);

  const handleDelete = async () => {
    if (!confirmRow) return;
    setDeleting(true);
    try {
      if (confirmRow.type === "purchase") {
        await axios.delete(`${api}/debts/${debt.id}/purchase/${confirmRow.id}`);
        toast.success(`Deleted purchase ${fmt(confirmRow.amount)}`);
      } else {
        // Payment row → underlying budget entry
        await axios.delete(`${api}/budget/${confirmRow.id}`);
        toast.success(`Deleted payment ${fmt(confirmRow.amount)}`);
      }
      setConfirmRow(null);
      reload();
      onDataChanged && onDataChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl rounded-sm border border-gray-200 max-h-[85vh] overflow-y-auto" data-testid="debt-payment-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-semibold tracking-tight">
              Payment History — {debt?.name}
            </DialogTitle>
          </DialogHeader>

          {loading && <div className="text-sm text-gray-500 py-6 text-center" data-testid="payment-history-loading">Loading payments…</div>}
          {err && <div className="text-sm text-red-600 py-3" data-testid="payment-history-error">{err}</div>}

          {data && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-4 pb-4 border-b border-gray-200">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total Paid</div>
                  <div className="font-mono-data text-lg font-semibold text-green-700" data-testid="payment-history-total">−{fmt(data.total_paid)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total Purchased</div>
                  <div className="font-mono-data text-lg font-semibold text-red-700" data-testid="payment-history-purchased">+{fmt(data.total_purchased || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Net Change</div>
                  <div className="font-mono-data text-lg font-semibold text-gray-700" data-testid="payment-history-net">{fmt((data.total_paid || 0) - (data.total_purchased || 0))}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Last Activity</div>
                  <div className="font-mono-data text-sm text-gray-700" data-testid="payment-history-last">{data.last_payment_date || "—"}</div>
                </div>
              </div>

              {data.payments.length === 0 ? (
                <div className="text-sm text-gray-400 py-6 text-center">No payments or purchases recorded against this debt yet.</div>
              ) : (
                <div className="border border-gray-200 rounded-sm overflow-x-auto">
                  <table className="spreadsheet-table w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-left">Date</th>
                        <th className="text-left">Description</th>
                        <th className="text-right">Amount</th>
                        <th className="text-center">Type</th>
                        <th className="text-center w-[80px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.map((p) => {
                        const isPurchase = p.type === "purchase";
                        return (
                          <tr key={p.id} data-testid={`payment-row-${p.id}`} className={isPurchase ? "bg-red-50/40" : ""}>
                            <td className="font-mono-data text-gray-700 whitespace-nowrap">{p.date}</td>
                            <td className="text-gray-700">
                              {p.description}
                              {isPurchase && p.interest_type && (
                                <span
                                  data-testid={`badge-int-${p.id}`}
                                  className={`ml-2 inline-flex items-center px-1.5 py-0 rounded-sm border text-[9px] font-mono-data uppercase tracking-wider ${
                                    p.interest_type === "standard"
                                      ? "border-red-300 text-red-700 bg-red-50"
                                      : "border-green-300 text-green-700 bg-green-50"
                                  }`}
                                >
                                  {p.interest_type === "standard" ? "Std APR" : "0%"}
                                </span>
                              )}
                            </td>
                            <td className={`numeric font-mono-data font-semibold ${isPurchase ? "text-red-700" : "text-green-700"}`}>
                              {isPurchase ? "+" : "−"}{fmt(p.amount)}
                            </td>
                            <td className="text-center">
                              {isPurchase ? (
                                <Badge data-testid="badge-purchase" className="border border-red-300 text-red-700 bg-red-50 text-[10px] px-1.5 py-0 rounded-sm font-mono-data" variant="outline">Purchase</Badge>
                              ) : p.linked ? (
                                <Badge className="border border-green-300 text-green-700 bg-green-50 text-[10px] px-1.5 py-0 rounded-sm font-mono-data" variant="outline">Payment</Badge>
                              ) : (
                                <Badge className="border border-gray-300 text-gray-600 bg-gray-50 text-[10px] px-1.5 py-0 rounded-sm font-mono-data" variant="outline">Match</Badge>
                              )}
                            </td>
                            <td className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                {isPurchase && (
                                  <button
                                    type="button"
                                    onClick={() => setEditing(p)}
                                    data-testid={`edit-purchase-${p.id}`}
                                    title="Edit purchase"
                                    className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-sm transition-colors"
                                  >
                                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setConfirmRow(p)}
                                  data-testid={`delete-row-${p.id}`}
                                  title={isPurchase ? "Delete purchase" : "Delete payment"}
                                  className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Edit Purchase modal ─────────────────────────── */}
      <DebtPurchaseDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        debt={debt}
        api={api}
        editPurchase={editing}
        onSaved={() => { setEditing(null); reload(); onDataChanged && onDataChanged(); }}
      />

      {/* ── Delete confirmation ─────────────────────────── */}
      <Dialog open={!!confirmRow} onOpenChange={(o) => !o && setConfirmRow(null)}>
        <DialogContent className="sm:max-w-md rounded-sm border border-gray-200" data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-semibold tracking-tight text-red-700">
              Delete {confirmRow?.type === "purchase" ? "Purchase" : "Payment"}?
            </DialogTitle>
          </DialogHeader>
          {confirmRow && (
            <div className="text-sm text-gray-700 space-y-2 py-2">
              <p>This will permanently delete the {confirmRow.type === "purchase" ? "purchase" : "payment"} row and reverse its effect on this debt's balance:</p>
              <div className="border border-gray-200 bg-gray-50 rounded-sm p-2.5 font-mono-data text-xs">
                <div><span className="text-gray-500">Date:</span> {confirmRow.date}</div>
                <div><span className="text-gray-500">Description:</span> {confirmRow.description}</div>
                <div><span className="text-gray-500">Amount:</span> {confirmRow.type === "purchase" ? "+" : "−"}{fmt(confirmRow.amount)}</div>
              </div>
              <p className="text-xs text-gray-500">This action cannot be undone.</p>
            </div>
          )}
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setConfirmRow(null)} data-testid="confirm-delete-cancel" disabled={deleting}>Cancel</Button>
            <Button
              type="button"
              onClick={handleDelete}
              data-testid="confirm-delete-submit"
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
